import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiError } from '@/types/api';
import { APP_CONFIG } from '@/config/app';
import { extractDrfResponseMessage } from '@/utils/errorUtils';

const ACCESS_TOKEN_KEY = 'auth_access_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';

const getAuthToken = (): string | null => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

const isTokenRefreshRequest = (url?: string): boolean => {
  return typeof url === 'string' && url.includes('/auth/token/refresh');
};

const shouldRefreshAfterUnauthorized = (url?: string): boolean => {
  if (typeof url !== 'string') {
    return true;
  }

  // Do not retry with refresh when 401 was from token obtain or token/refresh
  return !url.includes('/auth/login') && !url.includes('/auth/token/refresh');
};

const setAuthTokens = (access: string, refresh: string): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, access);
  localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
};

const updateAuthTokens = (access: string, refresh?: string): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, access);
  if (refresh) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  }
};

const removeAuthToken = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

const apiClient: AxiosInstance = axios.create({
  baseURL: APP_CONFIG.API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: ApiError | null, newAccess: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(newAccess);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.request.use(
  config => {
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    if (!isTokenRefreshRequest(config.url) && shouldRefreshAfterUnauthorized(config.url)) {
      const access = getAuthToken();
      if (access) {
        config.headers.Authorization = `Bearer ${access}`;
      }
    }
    return config;
  },
  error => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async error => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl =
      typeof originalRequest?.url === 'string' ? originalRequest.url : undefined;
    const responseData = error.response?.data;
    const apiError: ApiError = {
      message:
        extractDrfResponseMessage(responseData) ||
        error.message ||
        'An error occurred',
      status: status ?? 500,
      details: responseData,
    };

    if (status === 401 && !(originalRequest as { _retry?: boolean })._retry) {
      if (
        isTokenRefreshRequest(requestUrl) ||
        !shouldRefreshAfterUnauthorized(requestUrl)
      ) {
        removeAuthToken();
        window.location.href = '/login';
        return Promise.reject(apiError);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (value: unknown) => {
              if (typeof value === 'string') {
                originalRequest.headers.Authorization = `Bearer ${value}`;
              }
              resolve(apiClient(originalRequest));
            },
            reject,
          });
        });
      }

      (originalRequest as { _retry?: boolean })._retry = true;
      isRefreshing = true;

      try {
        const refresh = getRefreshToken();
        const refreshPayload = refresh ? { refresh } : {};
        const { data } = await axios.post<{ access: string; refresh?: string }>(
          `${APP_CONFIG.API_BASE_URL}/auth/token/refresh/`,
          refreshPayload,
          {
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
          }
        );
        const newAccess = data.access;
        updateAuthTokens(newAccess, data.refresh);
        processQueue(null, newAccess);
        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return apiClient(originalRequest);
      } catch {
        processQueue(apiError, null);
        removeAuthToken();
        window.location.href = '/login';
        return Promise.reject(apiError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(apiError);
  }
);

export async function apiFetch<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.get<T>(url, config);
  return response.data;
}

export async function apiPost<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.post<T>(url, data, config);
  return response.data;
}

export async function apiPut<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.put<T>(url, data, config);
  return response.data;
}

export async function apiPatch<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.patch<T>(url, data, config);
  return response.data;
}

export async function apiDelete<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.delete<T>(url, config);
  return response.data;
}

export {
  apiClient,
  getAuthToken,
  getRefreshToken,
  isTokenRefreshRequest,
  removeAuthToken,
  setAuthTokens,
  updateAuthTokens,
  shouldRefreshAfterUnauthorized,
};
